import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { stripAttachedConnectorsBlock } from "../../../shared/connectors/attachedConnectors";
import { stripAttachedSkillsBlock } from "../../../shared/skills/attachedSkills";
import { stripAttachedStepsBlock } from "../../../shared/steps/attachedSteps";
import { playClick } from "../../lib/sounds";
import { Avatar, getUserColor } from "../Avatar";
import { Dropdown } from "../Dropdown";

const maxStripHeight = 300;

const NavContainer = styled("div")`
  position: absolute;
  right: -10px;
  top: 0;
  bottom: 0;
  width: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: translateX(4px);
  transition:
    opacity 150ms ease-out,
    transform 150ms ease-out;
  z-index: 1;
  pointer-events: none;

  &[data-visible="true"] {
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }
`;

const NavLine = styled("div")`
  width: 12px;
  min-height: 1px;
  flex-shrink: 0;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 0;
    height: 100%;
    width: 8px;
    transform: translateX(-50%);
    border-radius: 2px;
    background: var(--bar-color, var(--gray-600));
    opacity: 1;
  }

  &[data-ai="true"]::after {
    background: var(--alpha8);
    opacity: 1;
  }
`;

const Marker = styled("div")`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  justify-content: center;

  &::after {
    content: "";
    width: 12px;
    height: 100%;
    border-radius: 9999px;
    background: var(--text-primary);
  }
`;

const charsPerLine = 60;

function getBarHeight(text: string): number {
  if (!text) return 1;
  const lines = text.split("\n").reduce((total, line) => {
    return total + Math.max(1, Math.ceil(line.length / charsPerLine));
  }, 0);
  return Math.max(1, Math.min(lines, 20));
}

const codeContextRe = /<code-context[^>]*>\n[\s\S]*?\n<\/code-context>/g;

function hasUserContent(entry: ChatEntryWithSubTables) {
  return (
    entry.entryType === "UserMessage" &&
    !!(entry.userMessage?.text || entry.attachments.length > 0)
  );
}

function getPreviewText(text: string) {
  const stripped = stripAttachedConnectorsBlock(
    stripAttachedStepsBlock(
      stripAttachedSkillsBlock(text.replace(codeContextRe, ""))
    )
  );
  return stripped.length > 40 ? `${stripped.slice(0, 40)}…` : stripped;
}

type Props = {
  hidden?: boolean;
  entries: readonly ChatEntryWithSubTables[];
  scrollContainer: React.RefObject<HTMLDivElement | null>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
};

// Module-level so the compiler doesn't read the mutation as a render-scope write.
function setContainerScrollTop(el: HTMLDivElement, top: number) {
  el.scrollTop = top;
}

export function ChatMessageNav({
  hidden = false,
  entries,
  scrollContainer,
  wrapperRef,
}: Props) {
  const userMessages = useMemo(() => entries.filter(hasUserContent), [entries]);

  const allMessages = useMemo(
    () =>
      entries.filter(
        (e) => hasUserContent(e) || e.entryType === "AssistantMessage"
      ),
    [entries]
  );

  const [isScrollable, setIsScrollable] = useState(false);
  const [scrollFraction, setScrollFraction] = useState(0);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    },
    []
  );

  const resetHideTimer = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    const check = () => {
      // Don't hide if the dropdown popover is open — retry later
      if (document.querySelector("[data-popover]")) {
        hideTimer.current = setTimeout(check, 500);
        return;
      }
      setVisible(false);
    };
    hideTimer.current = setTimeout(check, 1500);
  }, []);

  // Detect mouse proximity to right edge of wrapper to show nav
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const proximityThreshold = 40;

    const onMove = (e: MouseEvent) => {
      const rect = wrapper.getBoundingClientRect();
      const distFromRight = rect.right - e.clientX;
      if (distFromRight >= 0 && distFromRight <= proximityThreshold) {
        setVisible(true);
        if (hideTimer.current) clearTimeout(hideTimer.current);
      } else {
        setVisible((v) => {
          if (v) resetHideTimer();
          return v;
        });
      }
    };

    const onLeave = () => {
      resetHideTimer();
    };

    wrapper.addEventListener("mousemove", onMove);
    wrapper.addEventListener("mouseleave", onLeave);
    return () => {
      wrapper.removeEventListener("mousemove", onMove);
      wrapper.removeEventListener("mouseleave", onLeave);
    };
  }, [wrapperRef, resetHideTimer]);

  useEffect(() => {
    const container = scrollContainer.current;
    if (!container) return;

    const update = () => {
      const maxScroll = container.scrollHeight - container.clientHeight;
      setIsScrollable(maxScroll > 0);
      setScrollFraction(maxScroll > 0 ? container.scrollTop / maxScroll : 0);
      const containerRect = container.getBoundingClientRect();
      const centerY = (containerRect.top + containerRect.bottom) / 2;
      let closestId: string | null = null;
      let closestDist = Infinity;
      for (const msg of userMessages) {
        const el = container.querySelector(
          `[data-user-message-id="${msg.id}"]`
        );
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const msgCenter = (rect.top + rect.bottom) / 2;
        const dist = Math.abs(msgCenter - centerY);
        if (dist < closestDist) {
          closestDist = dist;
          closestId = msg.id;
        }
      }
      setActiveId(closestId);
    };

    const onScroll = () => {
      update();
      // Keep visible while scrolling if already shown
      setVisible((v) => {
        if (v) resetHideTimer();
        return v;
      });
    };

    update();
    container.addEventListener("scroll", onScroll, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => {
      container.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [scrollContainer, resetHideTimer, userMessages]);

  const scrollTo = (id: string) => {
    const container = scrollContainer.current;
    if (!container) return;
    const el = container.querySelector(`[data-user-message-id="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const isGroupChat = useMemo(() => {
    const userIds = new Set<string>();
    for (const msg of userMessages) {
      const userId = msg.userMessage?.user?.id;
      if (userId) userIds.add(userId);
      if (userIds.size > 1) return true;
    }
    return false;
  }, [userMessages]);

  // Drag: relative delta from start, not absolute position
  const dragRef = useRef<{
    startY: number;
    startScrollTop: number;
    lastClickY: number;
  } | null>(null);
  const openerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    const container = scrollContainer.current;
    if (!container) return;
    dragRef.current = {
      startY: e.clientY,
      startScrollTop: container.scrollTop,
      lastClickY: e.clientY,
    };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    const container = scrollContainer.current;
    if (!drag || !container) return;
    const delta = e.clientY - drag.startY;
    // Dragging up (negative delta) scrolls down, matching strip direction
    const maxScroll = container.scrollHeight - container.clientHeight;
    const sensitivity = maxScroll / maxStripHeight;
    setContainerScrollTop(container, drag.startScrollTop - delta * sensitivity);
    if (Math.abs(e.clientY - drag.lastClickY) >= 16) {
      playClick();
      drag.lastClickY = e.clientY;
    }
  };

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    setDragging(false);
  }, []);

  // Scroll wheel on the strip scrolls the chat
  const onWheel = (e: React.WheelEvent) => {
    const container = scrollContainer.current;
    if (!container) return;
    setContainerScrollTop(container, container.scrollTop + e.deltaY);
  };

  const onContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const opener = openerRef.current?.closest("[class]")?.parentElement;
    if (opener) {
      opener.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
    }
  }, []);

  useEffect(() => {
    const popovers = document.querySelectorAll("[data-popover]");
    for (const popover of Array.from(popovers)) {
      const buttons = Array.from(popover.querySelectorAll("button"));
      if (buttons.length !== userMessages.length) continue;
      const idx = userMessages.findIndex((m) => m.id === activeId);
      for (const btn of buttons) btn.removeAttribute("data-highlighted");
      if (idx >= 0 && buttons[idx]) {
        buttons[idx].setAttribute("data-highlighted", "");
        buttons[idx].scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeId, userMessages]);

  const barHeights = useMemo(
    () =>
      allMessages.map((msg) => {
        const text =
          msg.entryType === "UserMessage"
            ? (msg.userMessage?.text ?? "")
            : (msg.assistantMessage?.text ?? "");
        return getBarHeight(text);
      }),
    [allMessages]
  );

  const gap = 4;
  const naturalHeight =
    barHeights.reduce((sum, h) => sum + h, 0) +
    (allMessages.length - 1) * gap +
    gap * 2;
  const clipHeight = maxStripHeight;
  const translateY = clipHeight / 2 - scrollFraction * naturalHeight;

  if (!isScrollable) return null;

  const sections = [
    {
      items: userMessages.map((msg) => ({
        type: "button" as const,
        key: msg.id,
        label:
          getPreviewText(msg.userMessage?.text ?? "") ||
          msg.attachments.map((a) => a.fileName).join(", "),
        left:
          isGroupChat && msg.userMessage?.user ? (
            <Avatar user={msg.userMessage.user} size={16} />
          ) : null,
        onClick: () => scrollTo(msg.id),
      })),
    },
  ];

  return (
    <NavContainer data-visible={(visible && !hidden) || undefined}>
      <Dropdown
        opener={
          <div
            ref={openerRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onMouseDown={(e) => {
              if (e.button === 0) e.stopPropagation();
            }}
            onWheel={onWheel}
            onContextMenu={onContextMenu}
            style={{
              height: clipHeight,
              width: 20,
              overflow: "hidden",
              position: "relative",
              cursor: dragging ? "grabbing" : "grab",
              touchAction: "none",
              maskImage:
                "linear-gradient(to bottom, transparent, black 16px, black calc(100% - 16px), transparent)",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap,
                padding: `${gap}px 0`,
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${translateY}px)`,
              }}
            >
              {allMessages.map((msg, i) => {
                const userId = msg.userMessage?.user?.id;
                const barColor =
                  isGroupChat && userId
                    ? `var(--${getUserColor(userId)}-600)`
                    : undefined;
                return (
                  <NavLine
                    key={msg.id}
                    data-ai={msg.entryType === "AssistantMessage" || undefined}
                    style={
                      {
                        height: barHeights[i] ?? 1,
                        "--bar-color": barColor,
                      } as React.CSSProperties
                    }
                  />
                );
              })}
            </div>
            <Marker />
          </div>
        }
        sections={sections}
        margin={6}
        align="left-center"
        maxWidth={280}
      />
    </NavContainer>
  );
}
